import React from 'react';
import SeoMeta from '../components/SeoMeta';
import ContactForm from '../components/ContactForm';
import { getCdnImage } from '../lib/imageUtils';

export default function Contact() {
  return (
    <div className="bg-slate-50 min-h-screen">
      <SeoMeta
        title="Kontakt os - ENTREPRENØR LANG ApS"
        description="Kontakt aut. kloakmester & entreprenør Martin Hovmand Lang på tlf. 40 30 97 08 eller martinl4894@gmail.com."
        keywords="kontakt, entreprenør lang, telefon, email, adresse, karlebyvej 32, øster ulslev, 4894"
      />

      {/* Hero */}
      <section className="bg-slate-900 text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/firma2.jpg', {
              width: 1920,
              quality: 80,
            })}
            alt="ENTREPRENØR LANG ApS kontakt baggrund"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-3 border border-emerald-500/30">
              <i className="fa-solid fa-envelope" aria-hidden="true" />
              Kom i kontakt med os
            </span>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
              Kontakt os
            </h1>
            <p className="text-slate-300 text-base sm:text-lg mt-3 leading-relaxed">
              Vi besvarer gerne spørgsmål, giver rådgivning om dit projekt eller udarbejder et uforpligtende prisoverslag.
            </p>
          </div>
        </div>
      </section>

      {/* Content & Form */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left info box */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-6">
              <h2 className="text-2xl font-extrabold text-slate-900">
                Virksomhedsoplysninger
              </h2>

              {/* Contact Points */}
              <div className="space-y-4 pt-2">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center flex-shrink-0">
                    <i className="fa-solid fa-phone text-base" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase font-bold text-slate-500">Telefon</h3>
                    <a
                      href="tel:40309708"
                      className="text-lg font-extrabold text-slate-900 hover:text-emerald-700 transition-colors whitespace-nowrap"
                    >
                      40 30 97 08
                    </a>
                    <p className="text-xs text-slate-500 mt-0.5">Træffes alle hverdage</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center flex-shrink-0">
                    <i className="fa-solid fa-envelope text-base" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase font-bold text-slate-500">E-mail</h3>
                    <a
                      href="mailto:martinl4894@gmail.com"
                      className="text-sm font-bold text-slate-900 hover:text-emerald-700 transition-colors whitespace-nowrap block"
                    >
                      martinl4894@gmail.com
                    </a>
                    <a
                      href="mailto:Martin@entreprenorlang.dk"
                      className="text-xs text-slate-500 hover:text-emerald-700 whitespace-nowrap block"
                    >
                      Martin@entreprenorlang.dk
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center flex-shrink-0">
                    <i className="fa-solid fa-id-card text-base" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase font-bold text-slate-500">Virksomhedsdata</h3>
                    <p className="text-sm font-bold text-slate-900">ENTREPRENØR LANG ApS</p>
                    <p className="text-xs text-slate-500">CVR-nr: 28498675</p>
                  </div>
                </div>
              </div>

              {/* Facebook card */}
              <div className="pt-2 border-t border-slate-100">
                <a
                  href="https://www.facebook.com/p/Entrepren%C3%B8r-Lang-100054529146504/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-emerald-50 transition-colors group border border-slate-200"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#1877F2] text-white flex items-center justify-center">
                    <i className="fa-brands fa-facebook-f text-sm" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900 group-hover:text-emerald-800">
                      Følg Entreprenør Lang på Facebook
                    </div>
                    <div className="text-[11px] text-slate-500">Se løbende opdateringer og fotos fra projekter</div>
                  </div>
                </a>
              </div>
            </div>

            {/* Quality Seals */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <h3 className="text-sm font-bold text-slate-900 mb-3">Garanti & Certificering</h3>
              <div className="flex items-center gap-4">
                <img
                  src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/dme-kloakmestergaranti.webp', {
                    width: 440,
                    quality: 90,
                  })}
                  alt="DM&E Kloakmestergaranti"
                  className="h-24 w-auto object-contain"
                />
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
